import { Check } from 'lucide-react'
import { Fragment } from 'react'
import { useTranslation } from 'react-i18next'

import { useFormat } from '@/i18n/useFormat'
import { cn } from '@/lib/cn'
import { placeName } from '@/lib/countries'
import type { LegRoutePoint, LegStatus } from '@/types/domain'

import { LegPlace } from './LegPlace'
import { overdue } from './overdue'
import { hasDeparted, reachedCount } from './routeChain'

/* The leg's route chain — where the cargo IS, point by point, and the control
 * that says so (issue #35).
 *
 * Three states, and every one of them is a SERVED fact rather than a reading of
 * the dates:
 *
 *   REACHED  — the point carries a `reachedAt`. Filled, with a tick, and the
 *              caption says whose word it is on: «факт» for an operator's
 *              report, «авто» for the worker advancing the position on the
 *              normative days. Those are different degrees of certainty about
 *              where a client's cargo is, and collapsing them would let an
 *              assumption be read back as a confirmation.
 *   CURRENT  — the point whose id the server sent as `currentPointId`. NOT the
 *              last reached point computed here: that is the same rule
 *              `LegsSection` states for `currentLeg`, and two definitions of
 *              where a shipment is part company the first time one of them is
 *              corrected backwards.
 *   AHEAD    — everything else. Outlined, with the date it is expected on.
 *
 * ── Why it is a column ───────────────────────────────────────────────────────
 *
 * It replaces `PointChain`, which drew the same three states in a ROW. The row
 * was not merely cramped: it gave each point `basis-24`, so a five-stop corridor
 * did not fit a phone at all — and this desk works from phones at border posts.
 * A column costs one row per point and gains the space each point needs to carry
 * a control, which is what this component exists for and the row had nowhere to
 * put.
 *
 * ── The disc is the control ──────────────────────────────────────────────────
 *
 * Pressing a point marks the cargo arrived THROUGH it — that point and every
 * one before it — and pressing a point already marked clears it and everything
 * after. Both are the same single submission of the whole chain, because a
 * prefix length is the only thing this component can express (`routeChain.ts`).
 * Marking one point at a time would let a client compose the gap the server
 * refuses.
 *
 * Before this, the ONLY way to record an arrival was the overdue strip's button
 * — which renders on `late && next`, and `next` is read off `currentPointId`.
 * A chain with nothing reached has no current point, so the strip never
 * appeared, and a leg that had just been given a corridor could not be advanced
 * from this screen at all. That dead end is the reason the disc is a button.
 *
 * ── What this component still refuses to decide ──────────────────────────────
 *
 * Where the cargo is. `currentPointId` and `nextPointDueAt` are served; the
 * only arithmetic here is one timestamp against the clock. Re-deriving the
 * position from the arrivals it can see would be the second definition of it
 * that `legnow` exists to prevent, on the surface where being wrong is most
 * expensive — it is the one somebody reads out to a client.
 *
 * And the status. A leg can be `waiting-border` while the last reached point is
 * Almaty: the truck is standing at a crossing it has not been cleared through.
 * The chain is the position, the status is the movement report, and one control
 * answering both would be wrong in one of them every time.
 */

/** An arrival INSTANT as a bare day and month, in the viewer's zone.
 *
 *  Neither of `useFormat`'s two date functions fits, and both failures are
 *  worth naming so nobody swaps this back:
 *
 *   · `dateShort` is for DATE-ONLY values (AD-19) — it splits on `-` and would
 *     read `2026-08-24T09:00:00Z` as day `24T09:00:00Z`, producing an Invalid
 *     Date and a thrown `RangeError` from Intl. A route point's `reachedAt` is
 *     a timestamptz, not a milestone date.
 *   · `timestamp` renders the clock time with it. Under a 22px disc on a chain
 *     of five points, «20 авг., 09:00» is a caption that wraps, and the minute
 *     an operator pressed the button is not what anybody reads a route for.
 *
 *  The zone is the VIEWER's, deliberately, rather than UTC: an arrival recorded
 *  at 02:00 in Ashgabat is the day the desk says it is. */
function reachedOn(value: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short' }).format(
    new Date(value),
  )
}

export function PointTimeline({
  points,
  currentPointId,
  nextPointDueAt,
  status,
  onMarkThrough,
  pending,
  /** `now` is injected so the "is the next point overdue" comparison is
   *  testable without moving the clock, the way the relative-time formatter
   *  takes one. Defaults to the real one. */
  now,
}: {
  points: LegRoutePoint[]
  currentPointId?: string
  nextPointDueAt?: string
  /** The leg's own status — read for ONE refusal, not for display: see
   *  `originHeld` below. */
  status: LegStatus
  /** Submits the whole chain with the first `count` points reached. Absent
   *  makes the timeline read-only, which is what a caller without the route
   *  capability passes. */
  onMarkThrough?: (count: number) => void
  pending?: boolean
  now?: Date
}) {
  const { t, i18n } = useTranslation()
  const format = useFormat()

  /* A chain of one is not a chain — it draws a single disc with nothing to
     travel between, which reads as a bug rather than as a route. The server
     refuses to store one for the same reason. */
  if (points.length < 2) return null

  const currentIndex = points.findIndex((point) => point.id === currentPointId)
  const marked = reachedCount(points)

  /* The one thing the disc cannot do, and it is a SERVER fact rather than a
     permission: on a departed leg `stampDeparture` re-marks the first point of
     any chain that has none, so clearing it here would be an act that undoes
     itself between the request and the answer. Offering a control that visibly
     does nothing is worse than not offering it — the reader concludes the
     tracking is broken. Correcting a wrong departure is a status change, and
     the title says so. */
  const originHeld = hasDeparted(status)

  return (
    <ol className="flex flex-col">
      {points.map((point, index) => {
        const reached = point.reachedAt !== undefined
        const current = point.id === currentPointId
        /* The segment INTO this point, tinted by whether the cargo has already
           travelled it. Read off the current point's position rather than off
           this point's own arrival, so the transition the cargo is on right now
           is the first untinted one. */
        const travelled = currentIndex >= 0 && index <= currentIndex

        /* Pressing a marked point CLEARS from it; pressing an unmarked one
           marks through it. Both land on a prefix length. */
        const clears = index < marked
        const target = clears ? index : index + 1
        const blocked = clears && index === 0 && originHeld

        /* The point as it is printed beside the disc: a stored code is named. */
        const named = placeName(point.name, i18n.language) ?? point.name
        const label = blocked
          ? t('cases.legPage.departureHeld')
          : clears
            ? t('cases.legPage.clearFrom', { point: named })
            : t('cases.legPage.markThrough', { point: named })

        return (
          <Fragment key={point.id}>
            {index > 0 ? (
              /* The transition, and the normative days beside it. The rail is
                 aria-hidden as a graphic: the days are repeated in words in the
                 point's own caption, so a screen reader meets the chain as a
                 list of places and dates rather than as a column of bars. */
              <li aria-hidden className="grid grid-cols-[44px_minmax(0,1fr)] gap-3">
                <span className="flex justify-center">
                  <span
                    className={cn(
                      'h-[18px] w-[2px] rounded-full',
                      travelled ? 'bg-brand' : 'bg-line',
                    )}
                  />
                </span>
                {point.plannedDaysFromPrevious === undefined ? null : (
                  <span className="text-text-muted asa-num flex items-center text-[11px] leading-none">
                    {t('cases.legPage.normDays', {
                      count: point.plannedDaysFromPrevious,
                    })}
                  </span>
                )}
              </li>
            ) : null}

            <li className="grid grid-cols-[44px_minmax(0,1fr)] items-center gap-x-3 sm:grid-cols-[44px_minmax(0,1fr)_auto]">
              {/* 44px of hit area around a 22px disc (UX-DR-27). The disc is the
                  smallest target in this product and the one most often pressed
                  with a thumb, at a border post, in gloves. */}
              {onMarkThrough ? (
                <button
                  type="button"
                  /* aria-disabled rather than `disabled`, deliberately, and for
                     the reason `Button.blockedReason` gives at length: a
                     disabled control cannot be hovered, focused or reached by a
                     screen reader, so the one reader who most needs to know WHY
                     is the one who cannot get to it. The refusal is in the
                     accessible name. */
                  aria-disabled={blocked || undefined}
                  disabled={pending}
                  aria-label={label}
                  title={label}
                  onClick={() => {
                    if (blocked || pending) return
                    onMarkThrough(target)
                  }}
                  className={cn(
                    'flex size-11 items-center justify-center rounded-full',
                    'transition-colors duration-[var(--dur-fast)]',
                    blocked
                      ? 'cursor-not-allowed'
                      : 'hover:bg-surface-hover cursor-pointer',
                    'disabled:cursor-not-allowed disabled:opacity-55',
                  )}
                >
                  <Disc reached={reached} current={current} />
                </button>
              ) : (
                <span className="flex size-11 items-center justify-center">
                  <Disc reached={reached} current={current} />
                </span>
              )}

              <span className="text-text-heading min-w-0 text-[14px] leading-snug font-medium">
                <LegPlace name={point.name} country={point.country} />
                {/* The kind (0062), unless it is the plain waypoint every point
                    was before the kind existed — naming that on every row would
                    be noise around the two kinds that matter at a border. */}
                {point.kind && point.kind !== 'waypoint' ? (
                  <span className="text-text-muted ml-1.5 text-[11.5px] font-normal">
                    {t(`routePoints.kinds.${point.kind}`)}
                  </span>
                ) : null}
              </span>

              {/* The one caption per point, and the three cases are three
                  different sentences rather than one date formatted three ways:
                  an arrival somebody reported, an arrival the system assumed,
                  and a date nothing has happened on yet. */}
              <span
                className={cn(
                  'asa-num col-start-2 text-[12px] leading-tight whitespace-nowrap sm:col-start-auto',
                  /* The attention tone belongs to the point that is LATE, which
                     is the one after the current one: its expected date has
                     passed and the cargo has not been declared there. The
                     current point is not late — the cargo is standing on it. */
                  index === currentIndex + 1 && overdue(nextPointDueAt, now)
                    ? 'text-attention'
                    : 'text-text-muted',
                )}
              >
                {point.reachedAt ? (
                  <>
                    {reachedOn(point.reachedAt, format.locale)}{' '}
                    <span className="text-text-muted">
                      {point.reachedSource === 'auto'
                        ? t('cases.legPage.pointReachedAuto')
                        : t('cases.legPage.pointReachedFact')}
                    </span>
                  </>
                ) : /* The expected date is only known for the point IMMEDIATELY
                      after the current one — it is the server's
                      `nextPointDueAt`, computed from the current arrival and one
                      transition's norm. Nothing further along gets a date,
                      because adding up the remaining norms here would be this
                      component inventing an ETA the server never stated. */
                index === currentIndex + 1 && nextPointDueAt ? (
                  t('cases.legPage.pointExpected', {
                    date: reachedOn(nextPointDueAt, format.locale),
                  })
                ) : (
                  ''
                )}
              </span>

              {current ? (
                <span className="sr-only">{t('cases.legPage.currentPoint')}</span>
              ) : null}
            </li>
          </Fragment>
        )
      })}
    </ol>
  )
}

/** The disc itself — the same three states whether or not it is pressable, so a
 *  reader without the route capability sees the chain the operator sees. */
function Disc({ reached, current }: { reached: boolean; current: boolean }) {
  return (
    <span
      aria-hidden
      className={cn(
        'flex size-[22px] shrink-0 items-center justify-center rounded-full border',
        reached
          ? 'border-positive bg-positive text-white'
          : current
            ? 'border-brand bg-brand ring-brand/20 text-white ring-4'
            : 'border-line bg-surface',
        /* A point that is BOTH reached and current — the ordinary state, the
           cargo standing where it last arrived — keeps the halo so the eye lands
           on it, and the tick so the arrival is not just a colour. */
        reached && current ? 'border-brand bg-brand ring-brand/20 ring-4' : '',
      )}
    >
      {reached ? <Check size={13} strokeWidth={2.5} /> : null}
    </span>
  )
}
